import { useMemo, useRef } from 'react';

import {
  useGetPreviousConnectorFeatureInvocationStatuses,
  useListCurrentConnectorInvocationStatusesByConnector,
} from '@/hooks/scoped';
import type { ConnectorInvocationStatus } from '@/types/core/connector-invocation-status';
import type { UUID } from '@/utils/uuid';

const TERMINAL_STATUSES = new Set(['completed', 'failed', 'stopped']);

export type TerminalMetrics = {
  collected: number;
  changed: number;
  deleted: number;
  failedCount: number;
  hasData: boolean;
  lastSync: Date | undefined;
};

/**
 * Pure computation: given statuses, previousStatuses, capturedTerminal, and a
 * set of collector IDs, resolve the "best" terminal status per feature and
 * accumulate metrics.
 */
export function computeTerminalMetrics(
  statuses: Record<string, ConnectorInvocationStatus>,
  previousStatuses: Record<UUID, ConnectorInvocationStatus | undefined>,
  capturedTerminal: Record<UUID, ConnectorInvocationStatus>,
  collectorIds: Set<UUID>
): TerminalMetrics {
  let collected = 0;
  let changed = 0;
  let deleted = 0;
  let failedCount = 0;
  let hasData = false;
  let lastSync: Date | undefined;

  for (const [fid, current] of Object.entries(statuses)) {
    if (!collectorIds.has(fid as UUID)) continue;

    let best: ConnectorInvocationStatus | undefined;
    if (TERMINAL_STATUSES.has(current.status)) {
      best = current;
    } else {
      best = previousStatuses[fid as UUID] ?? capturedTerminal[fid as UUID];
    }

    if (!best) continue;

    const completed = best.completedAt;
    if (completed && (!lastSync || completed > lastSync)) {
      lastSync = completed;
    }

    if (best.status === 'failed') failedCount++;

    if (best.status === 'completed') {
      const c =
        best.metrics?.collection?.entitiesCollected ?? best.processedCount;
      if (c != null) {
        collected += c;
        hasData = true;
      }
      const u = best.metrics?.reconciliation?.catalogApply?.upsertsApplied;
      if (u != null) {
        changed += u;
        hasData = true;
      }
      const d = best.metrics?.reconciliation?.catalogApply?.deletesApplied;
      if (d != null) {
        deleted += d;
        hasData = true;
      }
    }
  }

  return { collected, changed, deleted, failedCount, hasData, lastSync };
}

/**
 * Hook that resolves terminal statuses for a connector's features, capturing
 * terminal statuses from the polling endpoint so they aren't lost when the next
 * run starts.
 *
 * Returns the current statuses, resolved previous statuses, and the captured
 * terminal map — ready to pass into `computeTerminalMetrics`.
 */
export function useResolvedConnectorStatuses(
  tenantId: UUID,
  connectorId: UUID
) {
  const statusQuery = useListCurrentConnectorInvocationStatusesByConnector(
    tenantId,
    connectorId
  );
  const statuses = statusQuery.statuses;

  const sequenceMap = useMemo(() => {
    const map: Record<UUID, number> = {};
    for (const [featureId, s] of Object.entries(statuses)) {
      if (s.runSequence != null) {
        map[featureId as UUID] = s.runSequence;
      }
    }
    return map;
  }, [statuses]);

  const { previousStatuses } = useGetPreviousConnectorFeatureInvocationStatuses(
    tenantId,
    connectorId,
    sequenceMap
  );

  // Capture terminal statuses as they pass through the polling endpoint so we
  // never lose them when the next run starts.
  const capturedTerminal = useRef<Record<UUID, ConnectorInvocationStatus>>({});
  for (const [fid, s] of Object.entries(statuses)) {
    if (TERMINAL_STATUSES.has(s.status)) {
      capturedTerminal.current[fid as UUID] = s;
    }
  }

  return {
    statuses,
    previousStatuses,
    capturedTerminal: capturedTerminal.current,
    isPending: statusQuery.isPending,
  };
}
